import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";

import { useGetScholarshipsAllQuery } from "../../../services/admin/scholarships";
import { useGetStudentsByGradYearQuery } from "../../../services/admin/adminStudents";
import {
  useAssignAwardMutation,
  useUnassignAwardMutation,
} from "../../../services/admin/adminAwards";
import { NotificationContext } from "../../../context/NotificationContext";
import { useSocket } from "../../../context/SocketContext";

import StudentDetail from "./StudentDetail";
import { useGetAdminStatusQuery } from "../../../services/admin/admin";
import { useGetSchoolsQuery } from "../../../services/lookups";
import ScholarshipDetailCard from "./ScholarshipDetail";
import AffiliationModel from "./AffiliationModal";
import ScholarshipSelector from "./ScholarshipSelector";
import StudentSelector from "./StudentSelector";
import StudentSearchDialog from "./StudentSearchDialog";
import SelectionHeader from "./SelectionHeader";
import SummaryStatsCard from "./SummaryStatsCard";
import Dialogs from "./Dialogs";
import StudentNominations from "./StudentNominations";
import { useRankings } from "../../hooks/useRankings";
import { useScholarshipStats } from "../../hooks/useScholarshipStats";
import getAffiliatedStudents from "../../utils/affiliations";
import {
  toTitleCase,
  isAcademicScholarship,
  isBursaryScholarship,
  formatCurrency,
  debugLog,
  schoolColorsNanaimo,
  schoolColorsLadysmith,
} from "../../utils/helpers";
import { useDebouncedSocketEmit } from "../../hooks/useDebouncedSocketEmit";

const AdminRoles = {
  NLSF_ADMIN: [1, 2],
  RECORDER: [6, 7],
  SCHOOL_ADMIN: [3, 7],
  SEL_PANEL: [5],
};

const LOCAL_STORAGE_KEY = "selcomState";
const MAX_SCHOOL_ADMIN_PULL_ATTEMPTS = 3;

const calculateScholarshipStats = (scholarships) => {
  if (!scholarships?.length) return null;

  return scholarships.reduce(
    (stats, scholarship) => {
      const count = scholarship.awardHistory?.length || 0;
      const amount = scholarship.award_amount || 0;
      const totalAwards = scholarship.number_of_awards || 0;

      stats.given += count;
      stats.total += totalAwards;
      stats.amountGiven += count * amount;
      stats.amountTotal += totalAwards * amount;
      stats.remaining = stats.total - stats.given;
      stats.amountRemaining = stats.amountTotal - stats.amountGiven;

      return stats;
    },
    {
      given: 0,
      total: 0,
      amountGiven: 0,
      amountTotal: 0,
      remaining: 0,
      amountRemaining: 0,
    }
  );
};

const SelectionCommitteeTab = () => {
  const lastEmittedState = useRef(null);
  const hasRestoredFromLocalStorage = useRef(false);
  const { addNotification } = useContext(NotificationContext);
  const requestScholarshipInterval = useRef(null);
  const pullAttemptCount = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const socket = useSocket();

  const [affiliationModalOpen, setAffiliationModalOpen] = useState(false);
  const [affiliatedStudents, setAffiliatedStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [activeStudent, setActiveStudent] = useState(null);
  const [studentsToAward, setStudentsToAward] = useState(new Set());
  const [studentDropdown, setStudentDropdown] = useState(""); // 🔥 Track selected student in dropdown
  const [openDialog, setOpenDialog] = useState(false);
  const [newScholarship, setNewScholarship] = useState(null);
  const grad_year = new Date().getFullYear();
  const [maxAwardsDialogOpen, setMaxAwardsDialogOpen] = useState(false); // Track max awards dialog state
  const [viewStudent, setViewStudent] = useState(null);
  const [viewStudentModalOpen, setViewStudentModalOpen] = useState(false);
  const [studentSearchOpen, setStudentSearchOpen] = useState(false);
  const [confirmSwitchDialogOpen, setConfirmSwitchDialogOpen] = useState(false); // Track confirmation dialog state
  const { data: adminStatus } = useGetAdminStatusQuery();
  const [overrideMaxAwardsStudentId, setOverrideMaxAwardsStudentId] = useState(null);
  const selectedScholarshipRef = useRef(null);

  const [sharedAwardStats, setSharedAwardStats] = useState(null);
  const [selectedScholarshipId, setSelectedScholarshipId] = useState("");
  const [pendingScholarshipId, setPendingScholarshipId] = useState(null);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedAwardIdToUnassign, setSelectedAwardIdToUnassign] =
    useState(null);
  const [cachedAcademicRankings, setCachedAcademicRankings] = useState({});
  const [cachedNeedRankings, setCachedNeedRankings] = useState({});
  const [affiliationName, setAffiliationName] = useState("");
  const { data: schoolsData } = useGetSchoolsQuery();
  const debounceEmit = useDebouncedSocketEmit(socket);

  const hasRole = (id, roles) => roles.includes(id);
  const isNLSFAdmin = hasRole(adminStatus?.admin_type_id, AdminRoles.NLSF_ADMIN);
  const isSchoolAdmin = hasRole(adminStatus?.admin_type_id, AdminRoles.SCHOOL_ADMIN);
  const isRecorder = hasRole(adminStatus?.admin_type_id, AdminRoles.RECORDER);
  const isSelPanel = hasRole(adminStatus?.admin_type_id, AdminRoles.SEL_PANEL);
  const canDriveSelection = isRecorder;
  const canManageNominations = canDriveSelection || isSchoolAdmin;
  
  const canPushGlobalState = canDriveSelection;
  const canSearchStudents = !isSelPanel;

  const safeEmit = useCallback(
    (payload) => {
      if (!canPushGlobalState) return;
      debounceEmit(payload);
    },
    [canPushGlobalState, debounceEmit]
  );

  // Create a mapping of school IDs to school names dynamically
  const schoolMapping = useMemo(() => {
    if (!schoolsData?.data) return {};
    return schoolsData.data.reduce((acc, school) => {
      acc[school.id] = school.name;
      return acc;
    }, {});
  }, [schoolsData]);

  const schoolIdByName = useMemo(() => {
    if (!schoolsData?.data) return {};
    return schoolsData.data.reduce((acc, school) => {
      acc[String(school.name).toLowerCase()] = String(school.id);
      return acc;
    }, {});
  }, [schoolsData]);

  const adminSchoolIds = useMemo(
    () => new Set((adminStatus?.schoolIds || []).map(String)),
    [adminStatus?.schoolIds]
  );

  const getStudentSchoolId = useCallback(
    (student) => {
      if (student?.school_id != null) return String(student.school_id);
      if (student?.schoolId != null) return String(student.schoolId);
      if (student?.school) {
        return schoolIdByName[String(student.school).toLowerCase()];
      }
      return null;
    },
    [schoolIdByName]
  );

  const canManageStudentNomination = useCallback(
    (student) => {
      if (canDriveSelection) return true;
      if (!isSchoolAdmin) return false;

      const schoolId = getStudentSchoolId(student);
      return schoolId ? adminSchoolIds.has(schoolId) : false;
    },
    [adminSchoolIds, canDriveSelection, getStudentSchoolId, isSchoolAdmin]
  );

  const getSchoolName = (schoolId) =>
    schoolMapping[schoolId] || `School #${schoolId}`;

  const cityOptions = [
    { id: 1, name: "Nanaimo" },
    { id: 2, name: "Ladysmith" },
  ];
  const [city_id, setCity_id] = useState(cityOptions[0].id);
  const schoolColors =
    city_id === 1 ? schoolColorsNanaimo : schoolColorsLadysmith;

  const {
    data: studentsData,
    isLoading: loadingStudents,
    refetch: refetchStudents,
  } = useGetStudentsByGradYearQuery(
    { grad_year, city_id },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  ); // eslint-disable-next-line
  const { data: scholarshipsData, refetch: refetchScholarships } =
    useGetScholarshipsAllQuery(
      { city_id },
      {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
      }
    );
  const [awardScholarship] = useAssignAwardMutation();
  const [unassignAward] = useUnassignAwardMutation();

  useEffect(() => {
    selectedScholarshipRef.current = selectedScholarship;
  }, [selectedScholarship]);

  const setActiveScholarship = useCallback((scholarship) => {
    selectedScholarshipRef.current = scholarship;
    setSelectedScholarship(scholarship);
  }, []);

  const students = useMemo(() => studentsData?.data || [], [studentsData]);
  const nominationStudents = useMemo(() => {
    if (!isSchoolAdmin || canDriveSelection) return students;
    return students.filter(canManageStudentNomination);
  }, [canDriveSelection, canManageStudentNomination, isSchoolAdmin, students]);
  const selectedNominationStudent = useMemo(
    () =>
      nominationStudents.find(
        (student) => String(student.id) === String(studentDropdown)
      ),
    [nominationStudents, studentDropdown]
  );

  const { academicRankings, needRankings } = useRankings(students);
  const scholarships = useMemo(
    () => scholarshipsData?.data || [],
    [scholarshipsData]
  );
  const totalAwardedStats = useScholarshipStats(scholarships);
  const summaryToDisplay = totalAwardedStats || sharedAwardStats;

  const refreshSelectionData = useCallback(async () => {
    const [studentsResult, scholarshipsResult] = await Promise.all([
      refetchStudents(),
      refetchScholarships(),
    ]);

    return {
      students: studentsResult?.data?.data || [],
      scholarships: scholarshipsResult?.data?.data || [],
    };
  }, [refetchStudents, refetchScholarships]);

  // Find the index of the currently selected scholarship
  const selectedScholarshipIndex = scholarships.findIndex(
    (s) => s.id === selectedScholarship?.id
  );

  useEffect(() => {
    if (!selectedScholarship?.id || scholarships.length === 0) return;

    const freshScholarship = scholarships.find(
      (scholarship) => scholarship.id === selectedScholarship.id
    );

    if (freshScholarship && freshScholarship !== selectedScholarship) {
      setActiveScholarship(freshScholarship);
    }
  }, [scholarships, selectedScholarship, setActiveScholarship]);

  useEffect(() => {
    if (students.length === 0) return;

    setSelectedStudents((current) => {
      if (!current.length) return current;

      const freshStudentsById = new Map(
        students.map((student) => [student.id, student])
      );
      let changed = false;
      const refreshed = current.map((student) => {
        const freshStudent = freshStudentsById.get(student.id);
        if (!freshStudent) return student;
        if (freshStudent !== student) changed = true;
        return freshStudent;
      });

      return changed ? refreshed : current;
    });

    setActiveStudent((current) => {
      if (!current?.id) return current;
      return students.find((student) => student.id === current.id) || current;
    });
  }, [students]);

  // Get the next scholarship in order (if available)
  const nextScholarship =
    selectedScholarshipIndex !== -1 &&
    selectedScholarshipIndex < scholarships.length - 1
      ? scholarships[selectedScholarshipIndex + 1]
      : null;

  // 🔥 Automatically load affiliated students when a scholarship is selected
  const handleScholarshipChange = useCallback(
    (eOrId, { overrideState = null } = {}) => {
      const selectedId = typeof eOrId === "number" ? eOrId : eOrId.target.value;
      const selected = scholarships.find((s) => s.id === selectedId);
      if (!selected || selected === null) return;

      debugLog("🏁 Setting selectedScholarship", selected);

      setSelectedScholarshipId(selectedId);
      setActiveScholarship(selected);
      setActiveStudent(null);

      // 🔥 Check for Affiliation-based priorities
      const hasAffiliation =
        selected?.priority1 === "Affiliation" ||
        selected?.priority2 === "Affiliation" ||
        selected?.priority3 === "Affiliation";
      // ✅ Declare outside to fix scope
      let matchingStudents = [];

      if (hasAffiliation && selected?.id && students && students.length > 0) {
        const { matchingStudents, affiliationName: foundAffiliationName } =
          getAffiliatedStudents(selected, students);
          setAffiliatedStudents(matchingStudents);
          console.log("Matching Students", matchingStudents);
        setAffiliationName(foundAffiliationName);
      } else {
        setAffiliatedStudents([]);
        setAffiliationName(""); // 🔥 Reset name if no affiliation
      }

      // Only clear state if not restoring
      if (!overrideState && canDriveSelection) {
        setSelectedStudents([]);
        setStudentsToAward(new Set());
      } else if (overrideState) {
        setSelectedStudents(overrideState.selectedStudents || []);
        setStudentsToAward(new Set(overrideState.studentsToAward || []));
      }


      debugLog("🔍 Checking for Affiliated Students", hasAffiliation);
      debugLog("🔍 Students", students);

      safeEmit({
        selectedScholarship: selected,
        selectedStudents: overrideState?.selectedStudents || [],
        studentsToAward: Array.from(
          overrideState?.studentsToAward || []
        ),
        affiliatedStudents: hasAffiliation ? matchingStudents : [],
        activeStudent: overrideState?.activeStudent || null,
        city_id,
        totalAwardedStats,
      });
      
    // Persist to localStorage
      if (canDriveSelection) {
        const newState = {
          selectedScholarship: selected,
          selectedStudents: overrideState?.selectedStudents || [],
          studentsToAward: overrideState?.studentsToAward || [],
          city_id,
          activeStudent: overrideState?.activeStudent || null,
          affiliatedStudents: hasAffiliation ? matchingStudents : [],
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newState));
      }
    },
    [
      scholarships,
      students,
      city_id,
      safeEmit,
      totalAwardedStats,
      canDriveSelection,
      setActiveScholarship,
    ]
  );

  useEffect(() => {
    // Only the recorder restores and owns persisted live selection state.
    if (!canDriveSelection) return;
    if (hasRestoredFromLocalStorage.current) return;
    if (students.length === 0) return; // ⛔ wait for student data

    // Fetch from localStorage if there's any previously saved state
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!saved) {
      hasRestoredFromLocalStorage.current = true;
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(saved);
    } catch (error) {
      console.warn("Unable to restore selection committee state.", error);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      hasRestoredFromLocalStorage.current = true;
      return;
    }

    if (!parsed) {
      hasRestoredFromLocalStorage.current = true;
      return;
    }

    hasRestoredFromLocalStorage.current = true;
    
    // Check if the city_id in localStorage matches the current one
    const storedCityId = parsed.city_id || 1;

    // Set the local state from saved data, but allow city_id to be updated if needed
    setSelectedStudents(parsed.selectedStudents || []);
    setStudentsToAward(new Set(parsed.studentsToAward || []));
    setCity_id(storedCityId); // ✅ Use this to ensure proper city change detection
    setActiveStudent(parsed.activeStudent || null);

    if (parsed.selectedScholarship?.id) {
      if (scholarships.length > 0) {
          handleScholarshipChange(parsed.selectedScholarship.id, {
            overrideState: parsed,
          });      
      } else {
        setPendingScholarshipId(parsed.selectedScholarship.id);
      }
    }
  }, [canDriveSelection, handleScholarshipChange, scholarships, students.length]);

  useEffect(() => {
    if (!socket || !city_id) return; // Ensure socket and city_id are available

    // Join the city room immediately so recorder clients receive school
    // nomination broadcasts even while local selection state is restoring.
    socket.emit("joinRoom", city_id);
    
    // 🚫 Skip clearing state until localStorage has restored
    const isRestoring = !hasRestoredFromLocalStorage.current || !!pendingScholarshipId;


  if (canDriveSelection && isRestoring) {
    debugLog("🛑 Skipping clearing: still restoring from localStorage...");
    return;
  }
    if (canDriveSelection){

        // Clear state on city change to ensure a fresh load
        setSelectedStudents([]);
        setStudentsToAward(new Set());
        setActiveScholarship(null);
        setActiveStudent(null);
        setAffiliatedStudents([]);
        setStudentDropdown("");
      
      }

    // Emit or request city-specific data based on the city_id change
    if (canDriveSelection) {
      // Merge city_id with existing stored state, if any
      const existing = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
      const updated = {
        ...existing,
        city_id,
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
    }

    // Fetch fresh selection data for the new city.
    refreshSelectionData();

  }, [
    city_id,
    socket,
    refreshSelectionData,
    canDriveSelection,
    pendingScholarshipId,
    setActiveScholarship,
  ]);
  

  useEffect(() => {
    debugLog("🔁 Pending Scholarship ID", pendingScholarshipId);
    if (pendingScholarshipId && scholarships.length > 0) {
      const newScholarship = scholarships.find(
        (s) => s.id === pendingScholarshipId
      );
      if (
        selectedScholarship &&
        selectedScholarship.id === newScholarship?.id
      ) {
        return; // Already selected
      }

      handleScholarshipChange(pendingScholarshipId);
      setPendingScholarshipId(null);
    }
  }, [
    handleScholarshipChange,
    pendingScholarshipId,
    scholarships,
    selectedScholarship,
  ]);

  // Auto push when stats update
  useEffect(() => {
    const currentState = {
      selectedScholarship,
      selectedStudents,
      studentsToAward: Array.from(studentsToAward),
      activeStudent,
      totalAwardedStats,
    };

    // Prevent emitting if state hasn’t changed
    if (
      JSON.stringify(lastEmittedState.current) === JSON.stringify(currentState)
    ) {
      return;
    }

    lastEmittedState.current = currentState;

    debugLog("🔁 Total Awarded Stats", totalAwardedStats);
    if (totalAwardedStats) {
      safeEmit({
        selectedScholarship: selectedScholarship,
        selectedStudents: selectedStudents || [], // Ensure it's an array
        studentsToAward: Array.from(studentsToAward || new Set()), // Ensure it's a valid iterable (Set → Array)
        activeStudent: activeStudent,
        city_id,
        totalAwardedStats,
      });
    }
  }, [
    totalAwardedStats,
    safeEmit,
    city_id,
    selectedScholarship,
    selectedStudents,
    studentsToAward,
    activeStudent,
  ]);

  // Function to advance to the next scholarship
  const handleNextScholarship = () => {
    if (selectedStudents.length > 0) {
      setConfirmSwitchDialogOpen(true); // Show confirmation dialog if there are students assigned
    } else {
      proceedToNextScholarship();
    }
  };

  // Function to advance to the next scholarship
  const proceedToNextScholarship = () => {
    if (nextScholarship) {
      handleScholarshipChange(nextScholarship.id);
      safeEmit({
        selectedScholarship: nextScholarship,
        selectedStudents: [],
        studentsToAward: [],
        affiliatedStudents: [],
        activeStudent: null,
        city_id,
        totalAwardedStats,
      });
    }
  };

  
 useEffect(() => {
  debugLog("🔁 Selected Scholarship", selectedScholarship);
  if (!socket || !adminStatus) return;
  
  const applyStateUpdate = (newState) => {
    // Ensure updates are city-specific
    if (String(newState.city_id) !== String(city_id)) return; // Skip updates for other cities

    debugLog("🔥 Received State Update for City", newState.city_id);
    refreshSelectionData();
    
    if (!canDriveSelection) {
      debugLog("🔥 School Admin State Update", newState);
      setSelectedStudents(newState.selectedStudents || []);
      
      if (newState.selectedScholarship?.id) {
        setActiveScholarship({ ...newState.selectedScholarship });
      } else {
        setActiveScholarship(null);
      }

      setStudentsToAward(new Set(newState.studentsToAward || []));
      setActiveStudent(newState.activeStudent || null);
      setAffiliatedStudents(newState.affiliatedStudents || []);
      setSharedAwardStats(newState.totalAwardedStats || {});
    } else if (newState.selectedStudents) {
      const incomingSelectedStudents = newState.selectedStudents || [];
      const incomingSelectedIds = new Set(
        incomingSelectedStudents.map((student) => student.id)
      );

      setSelectedStudents(incomingSelectedStudents);
      setActiveStudent((current) =>
        current?.id && !incomingSelectedIds.has(current.id) ? null : current
      );
      setStudentsToAward((current) => {
        const updatedAwards = new Set(
          Array.from(current).filter((studentId) =>
            incomingSelectedIds.has(studentId)
          )
        );

        return updatedAwards;
      });
    }
  };

  const handleSchoolUpdate = (newState) => {
    if (String(newState.city_id) !== String(city_id)) return;

    debugLog("🔥 Received School Nomination Update", newState);
    refreshSelectionData();

    if (!canDriveSelection) {
      setSelectedStudents(newState.selectedStudents || []);
      setStudentsToAward(new Set(newState.studentsToAward || []));
      setActiveStudent(newState.activeStudent || null);
      setAffiliatedStudents(newState.affiliatedStudents || []);
      setSharedAwardStats(newState.totalAwardedStats || {});
      return;
    }

    const incomingSelectedStudents = newState.selectedStudents || [];
    const incomingSelectedIds = new Set(
      incomingSelectedStudents.map((student) => student.id)
    );

    setSelectedStudents(incomingSelectedStudents);
    setActiveStudent((current) =>
      current?.id && !incomingSelectedIds.has(current.id) ? null : current
    );

    setStudentsToAward((current) => {
      const updatedAwards = new Set(
        Array.from(current).filter((studentId) =>
          incomingSelectedIds.has(studentId)
        )
      );

      socket.emit("updateStatePush", {
        selectedScholarship: selectedScholarshipRef.current,
        selectedStudents: incomingSelectedStudents,
        studentsToAward: Array.from(updatedAwards),
        affiliatedStudents,
        activeStudent:
          activeStudent?.id && incomingSelectedIds.has(activeStudent.id)
            ? activeStudent
            : null,
        city_id,
        totalAwardedStats,
      });

      return updatedAwards;
    });
  };

  // Listen for city-specific updates from the socket
  const eventToListen = "updateStatePull";
  socket.on(eventToListen, applyStateUpdate);

  const eventToListen2 = "updateSchoolPull";
  socket.on(eventToListen2, handleSchoolUpdate);

  const eventToListen3 = "updateSchoolPush";
  socket.on(eventToListen3, handleSchoolUpdate);

  // Handle school admin requesting the state for their city
  socket.on("schoolAdminLoadCall", () => {
    if (!canDriveSelection) return;

    debugLog("📥 School admin requested state. Sending update...");
    socket.emit("updateStatePull", {
      selectedScholarship,
      selectedStudents,
      studentsToAward: Array.from(studentsToAward),
      affiliatedStudents,
      activeStudent,
      city_id,
      totalAwardedStats,
    });
  });

  // Cleanup the socket listeners when component is unmounted or dependencies change
  return () => {
    socket.off(eventToListen, applyStateUpdate);
    socket.off(eventToListen2, handleSchoolUpdate);
    socket.off(eventToListen3, handleSchoolUpdate);
    socket.off("schoolAdminLoadCall");
  };
}, [
  socket,
  adminStatus,
  selectedScholarship,
  selectedStudents,
  studentsToAward,
  affiliatedStudents,
  activeStudent,
  refreshSelectionData,
  totalAwardedStats,
	  isSchoolAdmin,
	  isSelPanel,
	  isRecorder,
	  canDriveSelection,
		  city_id, // Added city_id here to trigger re-run when city_id changes
      setActiveScholarship,
		]);

  // ✅ Request state on load if school admin
  useEffect(() => {
    debugLog("🔁 Admin Status", adminStatus);
    if (!socket || !adminStatus) return;

    if (
      !canDriveSelection &&
      (!selectedScholarship || !selectedScholarship.id)
    ) {
      if (requestScholarshipInterval.current) {
        clearInterval(requestScholarshipInterval.current);
      }

      pullAttemptCount.current = 0;

      requestScholarshipInterval.current = setInterval(() => {
        if (pullAttemptCount.current >= MAX_SCHOOL_ADMIN_PULL_ATTEMPTS) {
          clearInterval(requestScholarshipInterval.current);
          requestScholarshipInterval.current = null;
          console.warn("🛑 Max pull attempts reached. Stopping requests.");
          return;
        }

        debugLog("📡 School Admin requesting state from admin...", city_id);
        socket.emit("schoolAdminLoad", { city_id });
        pullAttemptCount.current++;
      }, 3000);

      return () => {
        if (requestScholarshipInterval.current) {
          clearInterval(requestScholarshipInterval.current);
          requestScholarshipInterval.current = null;
        }
      };
    }
  }, [
    socket,
    adminStatus,
    selectedScholarship,
    canDriveSelection,
    city_id,
  ]);

  const handleCityChange = (event) => {
    debugLog("📡 City changed to", event.target.value);
    const selectedCityId = event.target.value;
    setCity_id(selectedCityId);

    // Emit an event to join the city-specific room (the server should ensure only this city's data is sent)
    socket.emit('joinRoom', selectedCityId);
    debugLog("📡 Joined room for city ID:", selectedCityId);
  };

  const handleClientsUpdate = (event) => {
    safeEmit({
      selectedScholarship: selectedScholarship,
      selectedStudents: selectedStudents || [], // Ensure it's an array
      studentsToAward: Array.from(studentsToAward || new Set()), // Ensure it's a valid iterable (Set → Array)
      city_id,
      affiliatedStudents: affiliatedStudents || [],
      activeStudent: activeStudent,
      totalAwardedStats,
    });
  };

  /** ✅ Assign student when button is clicked */
  const addStudentToSelection = (student, { closeSearch = false } = {}) => {
    if (!canManageNominations) return;

    if (student && !canManageStudentNomination(student)) {
      addNotification(
        "School admins can only nominate students from their own school(s).",
        "warning"
      );
      return;
    }

    if (student && !selectedStudents.some((s) => s.id === student.id)) {
      setStudentDropdown(""); // Reset dropdown after adding
      setSelectedStudents((prev) => {
        const updated = [...prev, student];

         const stateToPersist = {
          selectedScholarship,
          selectedStudents: updated,
          studentsToAward: Array.from(studentsToAward),
          activeStudent,
          affiliatedStudents: affiliatedStudents || [],
          city_id,
        };

        emitScholarshipUpdate({
        ...stateToPersist,
          totalAwardedStats,
        });
        if (canDriveSelection) {
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToPersist));
        }
        
        return updated;
      });

      if (closeSearch) {
        setStudentSearchOpen(false);
      }

      return;
    }

    if (student) {
      addNotification("Student is already selected.", "info");
    }
  };

  const handleAssignStudent = () => {
    if (!canManageNominations) return;
    if (!studentDropdown) return; // If no student selected, do nothing
    debugLog("🎓 Assigning student", studentDropdown);
    addStudentToSelection(selectedNominationStudent);
  };

  const handleResetState = () => {
    if (!canDriveSelection) return;
    console.log("🔄 Resetting state...");
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setActiveScholarship(null);
    setSelectedScholarshipId(null);
    setSelectedStudents([]);
    setStudentsToAward(new Set());
    setAffiliatedStudents([]);
    setCity_id(1);
    setActiveStudent(null);

    if (socket) {
      debugLog("🔥 Resetting State...");
      safeEmit({
        selectedScholarship: null,
        selectedStudents: [],
        studentsToAward: [],
        affiliatedStudents: [],
        city_id,
        activeStudent: null,
        totalAwardedStats,
      });
    }
  };

  const handleSelectStudentDetails = (student) => {
    setActiveStudent((prevActive) => {
      const newActive = prevActive?.id === student.id ? null : student;

      safeEmit({
        selectedScholarship,
        selectedStudents,
        studentsToAward: Array.from(studentsToAward),
        activeStudent: newActive,
        affiliatedStudents: affiliatedStudents || [],
        city_id,
        totalAwardedStats,
      });

      return newActive;
    });
  };

  const handleRemoveStudent = (studentId) => {
    const student = selectedStudents.find((s) => s.id === studentId);
    if (!student || !canManageStudentNomination(student)) return;

    setSelectedStudents((prevStudents) => {
      const updatedStudents = prevStudents.filter((s) => s.id !== studentId);

      setStudentsToAward((prevAwards) => {
        const updatedAwards = new Set(prevAwards);
        updatedAwards.delete(studentId);

        // Emit the updated state once
        emitScholarshipUpdate({
          selectedScholarship,
          selectedStudents: updatedStudents,
          studentsToAward: Array.from(updatedAwards),
          activeStudent: activeStudent?.id === studentId ? null : activeStudent,
          city_id,
          totalAwardedStats,
        });

        return updatedAwards;
      });

      return updatedStudents;
    });

    if (activeStudent?.id === studentId) {
      setActiveStudent(null);
    }
  };

  const handleToggleAwardStudent = (studentId) => {
      if (!canDriveSelection) return;

      setStudentsToAward((prev) => {
        const updated = new Set(prev);

        if (updated.has(studentId)) {
          updated.delete(studentId);
        } else {
          const currentAssigned =
            selectedScholarship?.awardHistory?.length || 0;
          const allowed = selectedScholarship?.number_of_awards || 0;
          const totalAssigned = updated.size + currentAssigned;

          if (totalAssigned >= allowed && !overrideMaxAwardsStudentId) {
            setOverrideMaxAwardsStudentId(studentId); // ⬅️ store the one that exceeded limit
            setMaxAwardsDialogOpen(true);
            return prev;
          }

          updated.add(studentId);
        }

        safeEmit({
          selectedScholarship,
          selectedStudents,
          studentsToAward: Array.from(updated),
          activeStudent,
          affiliatedStudents,
          city_id,
          totalAwardedStats,
        });

        return updated;
      });
    };

    const handleOverrideNomination = () => {
      if (!canDriveSelection) return;

      setStudentsToAward((prev) => {
        const updated = new Set(prev);
        if (overrideMaxAwardsStudentId) {
          updated.add(overrideMaxAwardsStudentId);
        }

        safeEmit({
          selectedScholarship,
          selectedStudents,
          studentsToAward: Array.from(updated),
          activeStudent,
          affiliatedStudents,
          city_id,
          totalAwardedStats,
        });

        return updated;
      });

      setOverrideMaxAwardsStudentId(null);
    };
console.log("adminStatus", adminStatus);
  const handleAwardStudents = async () => {
    if (!canDriveSelection) return;

    if (!selectedScholarship || studentsToAward.size === 0) {
      addNotification(
        "Please select a scholarship and at least one student to award.",
        "warning"
      );
      return;
    }

    try {
      await Promise.all(
        Array.from(studentsToAward).map((studentId) =>
          awardScholarship({
            studentId,
            scholarshipId: selectedScholarship.id,
            awardAmount: selectedScholarship.award_amount,
            awardYear: new Date().getFullYear(),
            status: "assigned",
            override: false,
            scholarship_name: selectedScholarship.name,
            account: selectedScholarship.account,
          }).unwrap()
        )
      );
      addNotification("Scholarship(s) successfully awarded!", "success");
      // 🔥 Clear selectedStudents and studentsToAward locally
      const clearedSelectedStudents = [];
      const clearedStudentsToAward = new Set();

      setSelectedStudents(clearedSelectedStudents);
      setStudentsToAward(clearedStudentsToAward);

      // Refetch students and scholarships so award totals come from the server.
      const refreshed = await refreshSelectionData();
      const updatedScholarship = refreshed.scholarships.find(
        (s) => s.id === selectedScholarship.id
      );
      const refreshedStats =
        calculateScholarshipStats(refreshed.scholarships) || totalAwardedStats;

      if (updatedScholarship) {
        setActiveScholarship(updatedScholarship);

        safeEmit({
          selectedScholarship: updatedScholarship,
          selectedStudents: clearedSelectedStudents, // ✅ fresh value
          studentsToAward: Array.from(clearedStudentsToAward), // ✅ fresh value
          city_id,
          affiliatedStudents: affiliatedStudents || [],
          totalAwardedStats: refreshedStats,
          activeStudent: null,
        });
      }
    } catch (error) {
      addNotification(
        "Error awarding scholarships. Please try again.",
        "error"
      );
    }
  };

  const handleDialogClose = (proceed) => {
    setOpenDialog(false);
    if (proceed) {
      debugLog("🔄 Proceeding with scholarship change", newScholarship);
      handleScholarshipChange(newScholarship);
    }
    setNewScholarship(null);
  };

  const groupedStudents = useMemo(() => {
    return selectedStudents.reduce((groups, student) => {
      if (!groups[student.school]) {
        groups[student.school] = [];
      }
      groups[student.school].push(student);
      return groups;
    }, {});
  }, [selectedStudents]);

  const emitScholarshipUpdate = (update) => {
    if (!canManageNominations) return;
    if (!socket) return;

      // Ensure city_id is defined
  if (!city_id) {
    console.error("city_id is undefined! Cannot emit to city room.");
    return;
  }

    const basePayload = {
      ...update,
       city_id: city_id,
    };

    debugLog("🔥 Pushing Selection State", basePayload);
    if (canDriveSelection) {
      socket.emit("updateStatePush", basePayload);
      return;
    }

    const schoolPayload = { ...basePayload };
    delete schoolPayload.selectedScholarship;
    delete schoolPayload.selectedScholarshipId;
    delete schoolPayload.scholarship;

    socket.emit("updateSchoolPush", schoolPayload);
  };

  const handleConfirmUnassign = async () => {
    if (!canDriveSelection) return;

    try {
      await unassignAward({ awardID: selectedAwardIdToUnassign }).unwrap();
      addNotification("Award unassigned successfully.", "success");
      setConfirmationOpen(false);

      // Refetch students and scholarships so displayed totals are current.
      const refreshed = await refreshSelectionData();

      const updatedScholarship = refreshed.scholarships.find(
        (s) => s.id === selectedScholarship.id
      );
      const refreshedStats =
        calculateScholarshipStats(refreshed.scholarships) || totalAwardedStats;

      if (updatedScholarship) {
        setActiveScholarship(updatedScholarship);

        // 🔥 Push the updated state to all clients
        safeEmit({
          selectedScholarship: updatedScholarship,
          selectedStudents,
          studentsToAward: Array.from(studentsToAward),
          affiliatedStudents,
          city_id,
          activeStudent,
          totalAwardedStats: refreshedStats,
        });
      }
    } catch (error) {
      addNotification("Failed to unassign award. Please try again.", "error");
    }
  };

  const handleCancel = () => {
    setConfirmationOpen(false); // Close confirmation modal without making changes
  };

  const isScholarship = isAcademicScholarship(selectedScholarship);
  const isBursary = isBursaryScholarship(selectedScholarship);

  useEffect(() => {
    if (!selectedScholarship || students.length === 0) return;

    const shouldSkipUpdate =
      isAcademicScholarship(selectedScholarship) ||
      isBursaryScholarship(selectedScholarship);

    if (shouldSkipUpdate) {
      debugLog(
        "🔁 Skipping re-generation of rankings due to existing scholarship type"
      );
      return;
    }

    // ✅ Recalculate only if not a scholarship or bursary
    const newAcademicRankings = students
      .filter((s) => Number(s.GPA) || Number(s.GradePercent))
      .sort((a, b) => {
        const gpaDiff = (Number(b.GPA) || 0) - (Number(a.GPA) || 0);
        return gpaDiff !== 0
          ? gpaDiff
          : (Number(b.GradePercent) || 0) - (Number(a.GradePercent) || 0);
      })
      .slice(0, 10)
      .reduce((acc, s, i) => {
        acc[s.id] = i + 1;
        return acc;
      }, {});

    const newNeedRankings = students
      .filter((s) => Number(s.bursary_total_income))
      .sort(
        (a, b) =>
          Number(a.bursary_total_income) - Number(b.bursary_total_income)
      )
      .slice(0, 10)
      .reduce((acc, s, i) => {
        acc[s.id] = i + 1;
        return acc;
      }, {});

    setCachedAcademicRankings(newAcademicRankings);
    setCachedNeedRankings(newNeedRankings);
  }, [selectedScholarship, students]);
  console.log("nextScholarship", nextScholarship);
  return (
    <>
      {isSchoolAdmin && !isRecorder && (
        <Typography variant="body1" sx={{ mx: 2 }}>
          <strong>{adminStatus.adminFullName}</strong> Representing School(s):{" "}
          {adminStatus.schoolIds?.length > 0
            ? adminStatus.schoolIds.map((schoolId, index) => (
                <span key={schoolId}>
                  {index > 0 && ", "}
                  {getSchoolName(schoolId)}
                </span>
              ))
            : " No Assigned Schools"}
        </Typography>
      )}
      {(isRecorder || isNLSFAdmin || isSelPanel) && (
        <Typography variant="body1" sx={{ mx: 2 }}>
          <strong>{adminStatus.adminFullName}: </strong> {adminStatus.adminTypeName}{" "}
        </Typography>
      )}

      {selectedScholarship && (
<Box
  sx={{
    position: "sticky",
    top: 0,
    zIndex: 1000,
    backgroundColor: "primary.light",
    color: "primary.contrastText",
    borderBottom: "1px solid #ccc",
    padding: "4px 8px",
  }}
>
  {/* First row */}
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
    }}
  >
    <Typography variant="subtitle2" fontWeight="bold">
      {selectedScholarship.name}
    </Typography>
    <Typography variant="body2">
      Amount: <strong>{formatCurrency(selectedScholarship.award_amount)}</strong>
    </Typography>
    <Typography variant="body2">
      Max Awards: <strong>{selectedScholarship.number_of_awards}</strong>
    </Typography>
  </Box>

  {/* Second row */}
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      mt: 0.5,
    }}
  >
    <Typography variant="caption">
      Priority 1: <strong>{selectedScholarship.priority1 || "—"}</strong>,&nbsp;
      Priority 2: <strong>{selectedScholarship.priority2 || "—"}</strong>,&nbsp;
      Priority 3: <strong>{selectedScholarship.priority3 || "—"}</strong>
    </Typography>
        <Typography variant="caption">
      Field of Study: <strong>{selectedScholarship.field_of_study || "—"}</strong>
    </Typography>
    <Typography variant="caption">
      Claim Period: <strong>{selectedScholarship.claim_period || "—"}</strong>
    </Typography>
  </Box>
</Box>
      )}

      <Box sx={{ width: "100%", padding: 2, mb: 5 }}>
        <SelectionHeader
	          isSchoolAdmin={isSchoolAdmin}
	          isRecorder={isRecorder}
	          isSelPanel={isSelPanel}
	          canDriveSelection={canDriveSelection}
          adminStatus={adminStatus}
          getSchoolName={getSchoolName}
          location={location}
          city_id={city_id}
          cityOptions={cityOptions}
          handleClientsUpdate={handleClientsUpdate}
          handleCityChange={handleCityChange}
          handleResetState={handleResetState}
          socket={socket}
          navigate={navigate}
        />

        <SummaryStatsCard summary={summaryToDisplay} />

        <Box sx={{ display: "flex", gap: 2, mt: 2, width: "100%" }}>
          <ScholarshipSelector
            adminStatus={adminStatus}
            selectedScholarship={selectedScholarship}
            selectedScholarshipId={selectedScholarshipId}
            selectedStudents={selectedStudents}
            scholarships={scholarships}
            setOpenDialog={setOpenDialog}
            setNewScholarship={setNewScholarship}
	            handleScholarshipChange={handleScholarshipChange}
	            canDriveSelection={canDriveSelection}
	          />
	          {canManageNominations && (
	            <>
              <StudentSelector
                isBursary={isBursary}
                isScholarship={isScholarship}
                adminStatus={adminStatus}
                selectedScholarship={selectedScholarship}
                studentDropdown={studentDropdown}
                setStudentDropdown={setStudentDropdown}
                students={nominationStudents}
                affiliatedStudents={affiliatedStudents}
                selectedStudents={selectedStudents}
                cachedAcademicRankings={cachedAcademicRankings}
                cachedNeedRankings={cachedNeedRankings}
                academicRankings={academicRankings}
                needRankings={needRankings}
                handleAssignStudent={handleAssignStudent}
                studentsToAward={studentsToAward}
                toTitleCase={toTitleCase}
              />

              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setStudentSearchOpen(true)}
	                disabled={!selectedScholarship?.id}
                sx={{
                  alignSelf: "flex-start",
                  minWidth: 150,
                  whiteSpace: "nowrap",
                }}
              >
                Search Students
              </Button>

              <Box sx={{ flex: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAssignStudent}
                  disabled={
                    !studentDropdown ||
                    !selectedNominationStudent ||
                    !selectedScholarship ||
                    (canDriveSelection &&
                      studentsToAward.size >=
                        selectedScholarship.number_of_awards)
                  }
                  sx={{ alignSelf: "flex-center", paddingX: "30px" }}
                >
                  Nominate
                </Button>
              </Box>
            </>
          )}
        </Box>

        {nextScholarship && (
	          <Button
	            variant="contained"
	            color="secondary"
	            onClick={canDriveSelection ? handleNextScholarship : null}
	            sx={{
	              mt: 1,
	              width: "100%",
	              pointerEvents: canDriveSelection ? "auto" : "none",
	            }}
          >
            <span>
              <strong>Next {nextScholarship.award_type}:</strong> {nextScholarship.name}
            </span>
          </Button>
        )}

        {selectedScholarship && Object.keys(selectedScholarship).length > 0 && (
          <ScholarshipDetailCard
	            selectedScholarship={selectedScholarship}
	            isSchoolAdmin={isSchoolAdmin}
	            isRecorder={isRecorder}
	            canDriveSelection={canDriveSelection}
	            setAffiliationModalOpen={setAffiliationModalOpen}
            setSelectedAwardIdToUnassign={setSelectedAwardIdToUnassign}
            setConfirmationOpen={setConfirmationOpen}
          />
        )}

        <StudentNominations
          loadingStudents={loadingStudents}
          schoolColors={schoolColors}
          groupedStudents={groupedStudents}
          activeStudent={activeStudent}
          studentsToAward={studentsToAward}
	          isSchoolAdmin={isSchoolAdmin}
	          isRecorder={isRecorder}
	          isSelPanel={isSelPanel}
	          canDriveSelection={canDriveSelection}
	          canManageNominations={canManageNominations}
	          canManageStudentNomination={canManageStudentNomination}
          selectedScholarship={selectedScholarship}
          adminStatus={adminStatus}
          handleSelectStudentDetails={handleSelectStudentDetails}
          toTitleCase={toTitleCase}
          setViewStudent={setViewStudent}
          setViewStudentModalOpen={setViewStudentModalOpen}
          handleToggleAwardStudent={handleToggleAwardStudent}
          handleRemoveStudent={handleRemoveStudent}
        />

        {selectedScholarship?.number_of_awards && (
          <Typography variant="body2" sx={{ ml: 1 }}>
            {(studentsToAward?.size || 0) +
              (selectedScholarship?.awardHistory?.length || 0)}
            /{selectedScholarship?.number_of_awards || 0}{" "}
            {selectedScholarship?.award_type === "bursary"
              ? "Bursar" +
                (selectedScholarship?.number_of_awards > 1 ? "ies" : "y")
              : "Award" +
                (selectedScholarship?.number_of_awards > 1 ? "s" : "")}{" "}
            Selected
          </Typography>
        )}
	        {canDriveSelection && studentsToAward.size > 0 && (
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAwardStudents}
          >
            Assign Selected Student{studentsToAward.size > 1 ? "s" : ""}
          </Button>
        )}

        {activeStudent && (
          <StudentDetail
            activeStudent={activeStudent}
            isBursary={isBursary}
            selectedScholarship={selectedScholarship}
            formatCurrency={formatCurrency}
            toTitleCase={toTitleCase}
          />
        )}
      </Box>
      {/* MODAL FOR STUDENT DETAIL */}

      <AffiliationModel
        open={affiliationModalOpen}
        onClose={() => setAffiliationModalOpen(false)}
        affiliatedStudents={affiliatedStudents}
        toTitleCase={toTitleCase}
        isSchoolAdmin={isSchoolAdmin}
	        isRecorder={isRecorder}
	        isSelPanel={isSelPanel}
	        canDriveSelection={canDriveSelection}
        adminStatus={adminStatus}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        affiliationName={affiliationName}
      />

      <Dialogs
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        confirmSwitchDialogOpen={confirmSwitchDialogOpen}
        setConfirmSwitchDialogOpen={setConfirmSwitchDialogOpen}
        proceedToNextScholarship={proceedToNextScholarship}
        maxAwardsDialogOpen={maxAwardsDialogOpen}
        handleOverrideNomination={handleOverrideNomination}
        setMaxAwardsDialogOpen={setMaxAwardsDialogOpen}
        selectedScholarship={selectedScholarship}
        viewStudentModalOpen={viewStudentModalOpen}
        setViewStudentModalOpen={setViewStudentModalOpen}
        viewStudent={viewStudent}
        selectedScholarshipForView={selectedScholarship}
        toTitleCase={toTitleCase}
        formatCurrency={formatCurrency}
        affiliationModalOpen={affiliationModalOpen}
        setAffiliationModalOpen={setAffiliationModalOpen}
        affiliatedStudents={affiliatedStudents}
        adminStatus={adminStatus}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        affiliationName={affiliationName}
        isConfirmationOpen={isConfirmationOpen}
        handleCancel={handleCancel}
        handleConfirmUnassign={handleConfirmUnassign}
      />

      <StudentSearchDialog
        open={studentSearchOpen && canSearchStudents}
        onClose={() => setStudentSearchOpen(false)}
        cityId={city_id}
        selectedStudents={selectedStudents}
        onSelectStudent={(student) =>
          addStudentToSelection(student, { closeSearch: true })
        }
        toTitleCase={toTitleCase}
      />
    </>
  );
};

export default SelectionCommitteeTab;
